import type { NetworkEntry } from "@anota/shared";
import { LIMITS } from "@anota/shared/limits";

/**
 * Peticiones de la página que fallan (HTTP 4xx/5xx o sin respuesta) desde que carga el widget: muchas
 * quejas ("no carga", "da error") salen de aquí. Solo método, URL sin query ni fragmento (pueden llevar
 * datos personales o tokens), estado y duración; nunca cabeceras ni cuerpos. Se guardan las últimas.
 */

const entries: NetworkEntry[] = [];
let installed = false;

/** Las del propio Anota no son del sitio. */
const OWN = /\/v1\/widget\//;

export function cleanUrl(raw: string): string {
  try {
    const u = new URL(raw, location.href);
    return `${u.origin}${u.pathname}`.slice(0, LIMITS.url);
  } catch {
    return String(raw).split(/[?#]/)[0]!.slice(0, LIMITS.url);
  }
}

function push(method: string, url: string, status: number, started: number): void {
  try {
    if (!url || OWN.test(url)) return;
    entries.push({ method: method.toUpperCase().slice(0, 10), url: cleanUrl(url), status, ms: Math.max(0, Math.round(performance.now() - started)), at: Date.now() });
    if (entries.length > LIMITS.networkEntries) entries.shift();
  } catch {
    // Nunca romper la página.
  }
}

type TrackedXhr = XMLHttpRequest & { __anota?: { method: string; url: string } };

export function installNetworkCapture(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  const originalFetch = window.fetch;
  if (typeof originalFetch === "function") {
    window.fetch = function (this: unknown, input: RequestInfo | URL, init?: RequestInit) {
      const started = performance.now();
      let method = "GET";
      let url = "";
      try {
        method = init?.method ?? (input instanceof Request ? input.method : "GET");
        url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
      } catch {
        // Lo que no se entienda, no se apunta.
      }
      return originalFetch.call(this, input, init).then(
        (res) => {
          if (res.status >= 400) push(method, url, res.status, started);
          return res;
        },
        (err: unknown) => {
          // Cancelarla (AbortController) no es un fallo de la web.
          if (!(err instanceof DOMException && err.name === "AbortError")) push(method, url, 0, started);
          throw err;
        }
      );
    } as typeof fetch;
  }

  const proto = typeof XMLHttpRequest !== "undefined" ? XMLHttpRequest.prototype : null;
  if (proto) {
    const open = proto.open;
    const send = proto.send;
    proto.open = function (this: TrackedXhr, method: string, url: string | URL, ...rest: unknown[]) {
      this.__anota = { method, url: String(url) };
      return (open as (...args: unknown[]) => void).call(this, method, url, ...rest);
    } as typeof proto.open;
    proto.send = function (this: TrackedXhr, body?: Document | XMLHttpRequestBodyInit | null) {
      const meta = this.__anota;
      if (meta) {
        const started = performance.now();
        this.addEventListener("load", () => {
          if (this.status >= 400) push(meta.method, meta.url, this.status, started);
        });
        this.addEventListener("error", () => push(meta.method, meta.url, 0, started));
        this.addEventListener("timeout", () => push(meta.method, meta.url, 0, started));
      }
      return send.call(this, body);
    };
  }
}

export const networkEntries = (): NetworkEntry[] => entries.slice();
